import { observable } from 'mobx';
import { getKeyBySelector } from './key-selector';
/**
 * Used within a reduce function for an
 * observable array to flatten a 2d array, e.g.:
 * ```
 *     const allSeats = area.rows
 *      .map(x => x.seats)
 *      .reduce(flattenObservableArray, observable.array([]));
 * ```
 */
export function flattenObservableArray(acc, next) {
    return observable.array([...acc, ...next]);
}
/**
 * Flattens a 2d array into a 1d array, when used in a
 * reduce function, e.g:
 * `[[1, 2], [3, 4].reduce(flattenArray, [])`
 * produces:
 * `[1, 2, 3, 4]`
 */
export function flattenArray(acc, next) {
    return [...acc, ...next];
}
export function compactMap(sourceArray, callbackfn) {
    const mapped = sourceArray.reduce((accumulator, currentValue, index, array) => {
        const mappedValue = callbackfn(currentValue, index, array);
        if (mappedValue !== undefined && mappedValue !== null)
            accumulator.push(mappedValue);
        return accumulator;
    }, []);
    return mapped;
}
export var SortOrder;
(function (SortOrder) {
    SortOrder["Ascending"] = "Ascending";
    SortOrder["Descending"] = "Descending";
})(SortOrder || (SortOrder = {}));
export function sortArrayByProperty(array, propertyName, order = SortOrder.Ascending) {
    if (!array)
        return [];
    return [...array].sort((a, b) => {
        if (a[propertyName] < b[propertyName])
            return order === SortOrder.Ascending ? -1 : 1;
        if (a[propertyName] > b[propertyName])
            return order === SortOrder.Ascending ? 1 : -1;
        return 0;
    });
}
export function convertArrayToMap(array, key) {
    return new Map(array.map(value => [value[key], value]));
}
/**
 * Converts an array to an object. For example:
 * ```
 * const example = [
 *     { id: 1, type: 1 },
 *     { id: 2, type: 1 },
 *     { id: 3, type: 2 }
 * ];
 * const exampleObjects = convertArrayToObject(example, 'id', x => x);
 * const exampleFetchedObjects = convertArrayToObject(example, 'id', x => createFetchedData(x, 60) );
 * console.log(exampleObjects);
 * ```
 * => {
 *     1: { id: 1, type: 1 },
 *     2: { id: 2, type: 1 },
 *     3: { id: 3, type: 2 },
 * }
 * @param array The array from which the object will be created.
 * @param keySelector The key for each value in the array.
 * @param mapperFn A function that maps each value
 */
export const convertArrayToObject = (array, keySelector, mapperFn) => {
    return array.reduce((acc, item) => {
        const key = getKeyBySelector(item, keySelector);
        acc[key] = mapperFn ? mapperFn(item) : item;
        return acc;
    }, {});
};
/**
 * Returns true if `array` is empty.
 */
export const isEmptyArray = (array) => {
    return array.length === 0;
};
/**
 * Returns true if `array` exists and is not empty.
 */
export const isNonEmptyArray = (array) => {
    return !!(array === null || array === void 0 ? void 0 : array.length);
};
/**
 * Wraps a given value in an array. Will return an empty array if the value is `undefined` or `null`.
 */
export const wrapInArray = (value) => {
    return value != null ? [value] : [];
};
/**
 * Groups the elements of an array according the specified key selector function. For example:
 * ```
 * const example = [
 *     { id: 1, type: 1 },
 *     { id: 2, type: 1 },
 *     { id: 3, type: 2 }
 * ];
 * groupBy(example, x => x.type);
 * console.log(example);
 * ```
 * => {
 *     1: [{ id: 1, type: 1 }, { id: 2, type: 1 }],
 *     2: [{ id: 3, type: 2 }],
 * }
 * @param array The array to group.
 * @param keySelector The function that returns a key for each object in the array to group by.
 */
export const groupArrayBy = (array, keySelector) => {
    return array.reduce((acc, item) => {
        const key = getKeyBySelector(item, keySelector);
        if (acc[key] === undefined)
            acc[key] = [];
        acc[key].push(item);
        return acc;
    }, {});
};
/**
 * Join elements inside of an array with a separator. For example:
 * ```
 * const example = ['A', 'B', 'C']
 * joinArrayWith(example, '-');
 * console.log(example);
 * ```
 * ['A', '-', 'B', '-', 'C']
 * @param array
 * @param separator
 */
export const joinArrayWith = (array, separator) => {
    return [...array].reduce((result, element, index, arr) => {
        result.push(element);
        if (index < arr.length - 1)
            result.push(separator);
        return result;
    }, []);
};
/**
 * If the item is an array, return the first element. Otherwise, return the item.
 */
export const getItemOrFirstElement = (item) => {
    if (Array.isArray(item))
        return item[0];
    return item;
};
/**
 * Returns an array of a given length, filled with the given item.
 */
export const createFilledArray = (item, length) => {
    return [...Array(length)].fill(item);
};
/**
 * Returns the distinct items in a given array.
 */
export const getDistinctItems = (array) => {
    return array.reduce((acc, item) => {
        const match = acc.findIndex(i => {
            if (typeof i === 'function')
                return i === item;
            return JSON.stringify(i) === JSON.stringify(item);
        }) !== -1;
        if (!match)
            acc.push(item);
        return acc;
    }, []);
};
